<template>
  <form @submit.prevent="handleSubmit" class="patron-form">
    <div class="form-field">
      <IntercomInput
        v-model="formData.name"
        :label="$t('patron.name')"
        placeholder="Enter patron name"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomPhoneInput
        v-model="formData.phone"
        :label="$t('patron.phone')"
        :disabled="loading"
        @update:valid="(v) => (phoneValid = v)"
      />
    </div>

    <div class="form-field">
      <IntercomEmailInput
        v-model="formData.email"
        :label="$t('patron.email')"
        placeholder="Enter email address"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.pan"
        label="PAN"
        placeholder="ABCDE1234F"
        :maxlength="10"
        :disabled="loading"
        class="pan-input"
      />
      <span class="field-hint">Required on 80G receipts so the donor can claim tax deduction.</span>
    </div>

    <div class="form-field">
      <IntercomSelect
        v-model="formData.type"
        :label="$t('patron.type')"
        :options="typeOptions"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.address"
        :label="$t('patron.address')"
        placeholder="Enter address"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.notes"
        :label="$t('patron.notes')"
        placeholder="Optional notes"
        :disabled="loading"
      />
    </div>

    <div class="form-actions">
      <IntercomButton type="button" variant="ghost" @click="$emit('cancel')" :disabled="loading">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton type="submit" variant="primary" :loading="loading" :disabled="formData.phone && !phoneValid">
        {{ $t('common.save') }}
      </IntercomButton>
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  IntercomInput,
  IntercomPhoneInput,
  IntercomEmailInput,
  IntercomSelect,
  IntercomButton,
} from '@shared/components/intercom'

const { t } = useI18n()

const props = defineProps({
  patron: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'cancel'])
const phoneValid = ref(false)

const formData = reactive({
  name: '',
  phone: '',
  email: '',
  pan: '',
  type: 'individual',
  address: '',
  notes: '',
})

const typeOptions = computed(() => [
  { value: 'individual', label: t('patron.types.individual') },
  { value: 'corporate', label: t('patron.types.corporate') },
  { value: 'foundation', label: t('patron.types.foundation') },
])

// Populate form when patron prop changes (edit mode)
watch(() => props.patron, (newPatron) => {
  if (newPatron) {
    formData.name = newPatron.name || ''
    formData.phone = newPatron.phone || ''
    formData.email = newPatron.email || ''
    formData.pan = newPatron.pan || ''
    formData.type = newPatron.patron_type || 'individual'
    formData.address = newPatron.address || ''
    formData.notes = newPatron.notes || ''
  }
}, { immediate: true })

const handleSubmit = () => {
  // Map form 'type' to API 'patron_type'
  const { type, ...rest } = formData
  // PAN is always uppercase by Indian tax convention; backend validates len=10 alphanum
  if (rest.pan) rest.pan = rest.pan.trim().toUpperCase()
  emit('submit', { ...rest, patron_type: type })
}
</script>

<style scoped>
.patron-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}

.pan-input :deep(input) {
  text-transform: uppercase;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}
</style>
